import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { otherBankInfoState } from '../../../../data/estimate/common/otherBank/info/selector';
import { SelfInputForNumber } from '../../../../types/common/SelfInput';
import { EstimateList } from '../../../../types/estimate/common/EstimateInfo';
import { MakerFinance } from '../../../../types/estimate/common/PaymentWay';
import useActiveTab from '../useActiveTab';
import { getCompareOtherBankList } from '../../../../api/estimate/common/paymentWay';
import { Brand, Trim } from '../../../../types/common/car/CarInfo';
import { selectCarInfoState } from '../../../../data/estimate/common/selectCar/info/selector';
import { otherBankMakerFinanceListState } from '../../../../data/estimate/common/otherBank/list/atom';
import { incidentalExpenseInfoState } from '../../../../data/estimate/common/incidentalExpense/info/selector';
import { exemptionOrTaxationInfoState } from '../../../../data/estimate/common/tax/info/selector';
import { Factory } from '../../../../types/estimate/new/ConsignmentInfo';
import { consignmentPriceInfoState } from '../../../../data/estimate/common/consignment/info/selector';
import { paymentChargeInfoState } from '../../../../data/estimate/common/paymentCharge/info/selector';
import { OtherBank } from '../../../../types/estimate/common/OtherBank';
import { refineOtherBankList } from '../../../../lib/estimate/common/otherBank/refineList';

interface InputData {
  inputName: string;
  inputValue: number;
}

/**
 *  타금융사 할부 목록 fetch custom hook
 *  */
function useFetchOtherBank(): {
  updateOtherBankList: ({ inputName, inputValue }: InputData) => void;
} {
  // tab
  const { id } = useActiveTab();

  // set
  const setOtherBankMakerFinanceList = useSetRecoilState(otherBankMakerFinanceListState);

  // select
  // 브랜드
  const brand: Brand = useRecoilValue(selectCarInfoState({ name: 'brand' }));
  const brandNo: number = brand?.brandNo || 0;

  // 트림
  const trim: Trim = useRecoilValue(selectCarInfoState({ name: 'trim' }));
  const isHybrid: boolean = JSON.parse(trim?.isHybrid); // 하이브리드 여부
  const fuelType: string = trim?.newFuelTypeName || ''; // 연료종류(전기, 수소, 경유, 휘발유 등등)
  // 등록합계
  const registrationTotalPrice: number = useRecoilValue(
    incidentalExpenseInfoState({ name: 'registrationTotalPrice' })
  );
  // 합계(탁송료 제외)
  const totalPriceWithoutConsignmentPrice: number = useRecoilValue(
    exemptionOrTaxationInfoState({ name: 'totalPriceWithoutConsignmentPrice' })
  );
  // 출고장
  const factory: Factory = useRecoilValue(consignmentPriceInfoState({ name: 'factory' }));
  // 탁송료 직접 입력
  const selfConsignmentPrice: SelfInputForNumber = useRecoilValue(
    consignmentPriceInfoState({ name: 'selfConsignmentPrice' })
  );
  // 탁송료
  const consignmentPrice: number = !!selfConsignmentPrice?.active
    ? selfConsignmentPrice.number
    : factory?.price || 0;

  // 합계(탁송료 포함)
  const totalPriceWithConsignmentPrice: number = useRecoilValue(
    consignmentPriceInfoState({ name: 'totalPriceWithConsignmentPrice' })
  );
  // 세금계산서(인도금대납 반영전)
  const taxInvoice: number = useRecoilValue(consignmentPriceInfoState({ name: 'taxInvoice' }));

  // 계약금
  const depositInfo: number = useRecoilValue(paymentChargeInfoState({ name: 'deposit' }));
  const selfDepositInfo: SelfInputForNumber = useRecoilValue(
    paymentChargeInfoState({ name: 'selfDeposit' })
  );
  const deposit: number = selfDepositInfo?.active ? selfDepositInfo.number : depositInfo;

  // 의무보험료
  const insuarancePremiumInfo: number = useRecoilValue(
    paymentChargeInfoState({ name: 'insuarancePremium' })
  );
  const selfInsuarancePremiumInfo: SelfInputForNumber = useRecoilValue(
    paymentChargeInfoState({ name: 'selfInsuarancePremium' })
  );
  const insuarancePremium: number = selfInsuarancePremiumInfo?.active
    ? selfInsuarancePremiumInfo.number
    : insuarancePremiumInfo;

  // 구분
  const type: number = useRecoilValue(otherBankInfoState({ name: 'otherBankType' }));
  // 기간
  const period: number = useRecoilValue(otherBankInfoState({ name: 'otherBankPeriod' }));
  // 기간 직접입력
  const selfPeriod: SelfInputForNumber = useRecoilValue(
    otherBankInfoState({ name: 'otherBankSelfPeriod' })
  );
  // 선수율
  const advanceReceivedRate: number = useRecoilValue(
    otherBankInfoState({ name: 'otherBankAdvanceReceivedRate' })
  );

  /**
   *  타금융사 할부비교 테이블에서 구분, 기간, 선수율이 변동될 때, 타금융사 할부 목록 요청을 하는 함수
   * @param {inputData} inputData 업데이트명(type or period or advanceReceivedRate), 업데이트값
   *  */
  const updateOtherBankList = useCallback(
    ({ inputName, inputValue }: InputData): void => {
      // 적용될 구분 타입
      const applyType: number = inputName === 'type' ? inputValue : type;

      // 적용될 기간
      const applyPeriod: number =
        inputName === 'period' ? inputValue : !!selfPeriod.active ? selfPeriod.number : period;

      // 적용될 선수욜
      const applyAdvanceReceivedRate: number =
        inputName === 'advanceReceivedRate' ? inputValue : advanceReceivedRate;

      (async () => {
        // 타금융사 할부 메이커금융 상품 목록 요청
        const compareOtherBankList: MakerFinance[] = await getCompareOtherBankList(
          brandNo,
          applyAdvanceReceivedRate,
          applyPeriod,
          applyType,
          isHybrid,
          fuelType
        );

        const productList: MakerFinance[] = compareOtherBankList;

        // 타금융할부 목록
        const oherBankList: OtherBank[] = trim
          ? refineOtherBankList({
              trim,
              registrationTotalPrice,
              totalPriceWithoutConsignmentPrice,
              consignmentPrice,
              totalPriceWithConsignmentPrice,
              taxInvoice,
              deposit,
              insuarancePremium,
              productList,
              period: applyPeriod,
              advanceReceivedRate: applyAdvanceReceivedRate,
            }).sort((a: OtherBank, b: OtherBank) => {
              // 월납입금 오름차순
              // return a.monthlyPayment - b.monthlyPayment;
              // 금리 오름차순
              return a.interestRate - b.interestRate;
            })
          : [];

        setOtherBankMakerFinanceList((prev: EstimateList<OtherBank>[]) =>
          prev.map((state: EstimateList<OtherBank>) =>
            state.id === id ? { ...state, list: oherBankList } : state
          )
        );
      })();
    },
    [id, brandNo, type, period, selfPeriod, advanceReceivedRate]
  );

  return { updateOtherBankList };
}

export default useFetchOtherBank;
